import {
  Box,
  Button,
  Checkbox,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { FaPen, FaLock } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteOrgUsers,
  getOrgUsers,
} from '../../../stores/slices/organisations/organisationsActions';
import { filterByValue } from '../../../utils/filterByValue';
import DeleteAlert from '../../Alerts/DeleteAlert';
import UserModal from './UserModal';
import UserPasswordModal from './UserPasswordModal';

const OrgUsers = ({ organisation }) => {
  const { userInfo } = useSelector(state => state.user);
  const dispatch = useDispatch();
  const [users, setUsers] = useState([]);

  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [toUpdate, setToUpdate] = useState({ state: false, user: null });
  const [toPasswordUpdate, setPasswordToUpdate] = useState({ state: false, user: null });
  const [isDeleting, setIsDeleting] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const cancelRef = useRef();
  const searchRef = useRef();
  const {
    isOpen: isUserModalOpen,
    onOpen: onUserModalOpen,
    onClose: onUserModalClose,
  } = useDisclosure();
  
  const {
    isOpen: isPasswordModalOpen,
    onOpen: onPasswordModalOpen,
    onClose: onPasswordModalClose,
  } = useDisclosure();
  
  const {
    isOpen: isOpenAlert,
    onOpen: onOpenAlertAlert,
    onClose: onCloseAlert,
  } = useDisclosure();
  const toast = useToast();
  const getUsers = async () => {
    const res = await dispatch(getOrgUsers({ orgId: organisation.id }));
    setUsers(res.payload.data);

    setIsLoading(false);
  };
  useEffect(() => {
    setIsLoading(true);

    getUsers();
  }, []);
  useEffect(() => {
    setFilteredUsers(users);
  }, [users]);

  const handleSelectAll = event => {
    let newSelectedUsers;

    if (event.target.checked) {
      newSelectedUsers = users.map(user => user.id);
    } else {
      newSelectedUsers = [];
    }

    setSelectedUsers(newSelectedUsers);
  };
  const handleSelectOne = (event, id) => {
    const selectedIndex = selectedUsers.indexOf(id);
    let newSelectedUsers = [];

    if (selectedIndex === -1) {
      newSelectedUsers = newSelectedUsers.concat(selectedUsers, id);
    } else if (selectedIndex === 0) {
      newSelectedUsers = newSelectedUsers.concat(selectedUsers.slice(1));
    } else if (selectedIndex === selectedUsers.length - 1) {
      newSelectedUsers = newSelectedUsers.concat(selectedUsers.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelectedUsers = newSelectedUsers.concat(
        selectedUsers.slice(0, selectedIndex),
        selectedUsers.slice(selectedIndex + 1)
      );
    }

    setSelectedUsers(newSelectedUsers);
  };
  const onCloseModal = () => {
    onUserModalClose();
    setToUpdate({ state: false, user: null });
  };
  const onPasswordCloseModal = () => {
    onPasswordModalClose();
    setPasswordToUpdate({ state: false, user: null });
  };
  const onDeleteItem = async () => {
    setIsDeleting(true);
    const res = await dispatch(deleteOrgUsers({ selectedUsers }));
    onCloseAlert();
    setIsDeleting(false);
    if (res.type === 'organisations/deleteOrgUsers/rejected') {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      await getUsers();
      setSelectedUsers([]);
      toast({
        description: 'User has been deleted',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
  };

  const filter = data => {
    const text = data.target.value;
    setFilteredUsers(filterByValue(users, text));
  };

  const clearInput = () => {
    searchRef.current.value = '';
    setFilteredUsers(users);
  };

  return (
    <React.Fragment>
      <Box w={'full'} overflowX={'hidden'} overflowY={'auto'} mt={'2'}>
        <VStack spacing={'4'}>
          <Stack
            justifyContent={'space-between'}
            alignContent={'center'}
            direction={{ base: 'column', sm: 'row' }}
            w={'full'}
            spacing={4}
            pr={'2'}
            pb={'2'}
            zIndex={'10'}
            sx={{
              position: '-webkit-sticky',
              /* Safari */
              position: 'sticky',
              top: '0',
            }}
            bg={'white'}
            borderBottom={'1px'}
            borderColor={'gray.500'}
          >
            <InputGroup size="sm" maxW={{ base: '100%', md: '40%' }}>
              <Input
                pr="4.5rem"
                placeholder="Search"
                borderRadius={'4'}
                onChange={filter}
                ref={searchRef}
              />
              <InputRightElement width="4.5rem" justifyContent={'end'}>
                <Button
                  size={'sm'}
                  borderLeftRadius={'none'}
                  onClick={clearInput}
                >
                  Clear
                </Button>
              </InputRightElement>
            </InputGroup>
            <HStack
              spacing={2}
              w={'full'}
              maxW={{ base: '100%', md: '40%' }}
              align={'start'}
              justify={'end'}
            >
              <Button size={'sm'} onClick={onUserModalOpen}>
                Add User
              </Button>
              <Button
                size={'sm'}
                disabled={selectedUsers.length <= 0}
                bg={'red.400'}
                _hover={{
                  color: 'red.400',
                  bg: 'transparent',
                }}
                onClick={onOpenAlertAlert}
              >
                Remove selected users
              </Button>
            </HStack>
          </Stack>
          <Box overflowX={'auto'} w={'full'}>
            <TableContainer>
              <Table
                variant="striped"
                colorScheme="gray"
                display={{
                  base: 'block',
                  md: 'table',
                }}
              >
                <Thead>
                  <Tr>
                    <Th>
                      <Checkbox
                        value="true"
                        onChange={handleSelectAll}
                        isChecked={selectedUsers.length === users.length}
                      />
                    </Th>
                    <Th>Name</Th>
                    <Th>Email</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {users &&
                    (isLoading ? (
                      <Tr>
                        <Td py={'1'} colSpan={'5'}>
                          <Spinner color="primary" />
                        </Td>
                      </Tr>
                    ) : users.length > 0 ? (
                      filteredUsers.map(user => (
                        <Tr key={user.id}>
                          <Td py={'1'}>
                            <Checkbox
                              value="true"
                              disabled={userInfo.id === user.id}
                              isChecked={selectedUsers.indexOf(user.id) !== -1}
                              onChange={event =>
                                handleSelectOne(event, user.id)
                              }
                            />
                          </Td>
                          <Td py={'1'}>{user.full_name}</Td>
                          <Td py={'1'}>{user.email}</Td>
                          <Td py={'1'} align={'right'} textAlign={'right'}>
                            <HStack justify={'end'}>
                              <IconButton
                                icon={<FaPen />}
                                size={'sm'}
                                onClick={() => {
                                  setToUpdate({
                                    state: true,
                                    user,
                                  });
                                  onUserModalOpen();
                                }}
                              />
                              <IconButton
                                icon={<FaLock />}
                                size={'sm'}
                                onClick={() => {
                                  setPasswordToUpdate({
                                    state: true,
                                    user,
                                  });
                                  onPasswordModalOpen();
                                }}
                              />
                            </HStack>
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td py={'1'} colSpan={'5'}>
                          No users found
                        </Td>
                      </Tr>
                    ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        </VStack>
      </Box>
      <DeleteAlert
        isOpen={isOpenAlert}
        cancelRef={cancelRef}
        onClose={onCloseAlert}
        isDeleting={isDeleting}
        header={`Delete User${selectedUsers.length > 1 ? 's' : ''}`}
        body={
          "Are you sure? If you proceed with the deletion, it'll be gone forever."
        }
        onDeleteItem={onDeleteItem}
      />
      {isUserModalOpen && (
        <UserModal
          isOpen={isUserModalOpen}
          onOpen={onUserModalOpen}
          onClose={onCloseModal}
          toUpdate={toUpdate}
          org={organisation}
          getUsers={getUsers}
        />
      )}
      {isPasswordModalOpen && (
        <UserPasswordModal
          isOpen={isPasswordModalOpen}
          onClose={onPasswordCloseModal}
          toUpdate={toPasswordUpdate}
        />
      )}
    </React.Fragment>
  );
};

export default OrgUsers;
